import LoadingScreen from "./LoadingScreen";
import { Alert, AlertTitle, Button } from "@mui/material";
import { forwardRef, useEffect } from "react";
import { useAuthentication } from "./hooks/useAuthentication";

export const defaultStaleTime = 30 * 60 * 1000;// 30 minutes

export const GenericError = ({ error, onReload }) => <>
    <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        <div>{error.message}</div>
        <Button onClick={onReload} variant="contained">Reload</Button>
    </Alert>
</>;

/**
 * Renders loading / error UI for a React Query result, then calls `children` with resolved data.
 *
 * @param {object} props
 * @param {object} props.query - Result object from useQuery (or compatible hook)
 * @param {React.ComponentType} [props.error] - Optional custom error component. Receives:
 *   - error: The error object from the query
 *   - onReload: Function to refetch the query
 * @param {(data: *, reload: Function) => React.ReactNode} props.children - Render prop
 *
 * @example
 * <QueryData query={queryResults} error={MyError}>
 *   {(data, reload) => <MyContent data={data} reload={reload} />}
 * </QueryData>
 */
export const QueryData = ({ query, error: CustomError, children }) => {
    const { reauthenticate } = useAuthentication();
    const { data, error, isLoading, isError, isFetching, refetch } = query;

    useEffect(() => {
        if (isError && error?.cause?.status === 401) {
            reauthenticate();
        }
    }, [isError, error, reauthenticate]);

    if (isLoading || isFetching) {
        return <LoadingScreen />;
    }

    if (isError) {
        const ErrorComponent = CustomError ?? GenericError;
        return <ErrorComponent error={error} onReload={refetch} />;
    }

    return data ? children(data, refetch) : null;
};

/**
 * Renders loading / error UI for multiple React Query results, then calls `children` when all are ready.
 *
 * @param {object} props
 * @param {Record<string, object>} props.queries - Object mapping keys to query result objects
 * @param {React.ComponentType} [props.error] - Optional custom error component. Receives:
 *   - error: The error object from the first errored query
 *   - onReload: Function to refetch the errored query
 * @param {(queryResults: Record<string, object>, reloadAll: Function) => React.ReactNode} props.children - Render prop
 *
 * @example
 * <QueryMultipleData
 *   queries={{ reservation: reservationQuery, rooms: roomsQuery }}
 *   error={ReservationQueryError}
 * >
 *   {(queryResults, reloadAll) => (
 *     <MyContent
 *       reservation={queryResults.reservation.data}
 *       rooms={queryResults.rooms.data}
 *       reloadAll={reloadAll}
 *     />
 *   )}
 * </QueryMultipleData>
 */
export const QueryMultipleData = ({ queries, error: CustomError, children }) => {
    const { reauthenticate } = useAuthentication();

    const queryResultsArray = Object.values(queries);
    const isLoading = queryResultsArray.some((query) => query.isLoading);
    const isError = queryResultsArray.some((query) => query.isError);
    const queryResultsWithError = queryResultsArray.filter((query) => query.isError);

    useEffect(() => {
        if (isError && queryResultsWithError.some((queryResult) => queryResult.error?.cause?.status === 401)) {
            reauthenticate();
        }
    }, [isError, queryResultsWithError, reauthenticate]);

    const reloadAll = () => queryResultsArray.forEach((query) => query.refetch());

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (isError) {
        const ErrorComponent = CustomError ?? GenericError;
        const firstError = queryResultsWithError[0];
        return <ErrorComponent error={firstError.error} onReload={firstError.refetch} />;
    }

    return children(queries, reloadAll);
};

/**
 * HOC: withQueryData
 * @param {React.ComponentType} Component - The component to wrap. Should accept props:
 *   - data: The result data from the query
 *   - reload: Function to refetch the query
 *   - Any additional props passed to the HOC
 * @param {React.ComponentType} [CustomError] - Optional custom error component. Receives props:
 *   - error: The error object from the query
 *   - onReload: Function to refetch the query
 *
 * The HOC expects a prop:
 *   - queryData: The result object from a React Query hook (useQuery, etc.)
 */
export const withQueryData = (Component, CustomError) => {
    const WrappedComponent = forwardRef(({ queryData, ...props }, ref) => (
        <QueryData query={queryData} error={CustomError}>
            {(data, reload) => <Component data={data} reload={reload} {...props} ref={ref} />}
        </QueryData>
    ));

    return WrappedComponent;
};

/**
 * HOC: withMultipleQueryData
 * @param {React.ComponentType} Component - The component to wrap. Should accept props:
 *   - queryResults: An object mapping keys to query result objects
 *   - reloadAll: Function to refetch all queries
 *   - Any additional props passed to the HOC
 * @param {React.ComponentType} [CustomError] - Optional custom error component. Receives props:
 *   - error: The error object from the first errored query
 *   - onReload: Function to refetch the errored query
 *
 * The HOC expects a prop:
 *   - queryResults: An object of query result objects (from useQueries or multiple useQuery)
 */
export const withMultipleQueryData = (Component, CustomError) => {
    const WrappedComponent = ({ queryResults, ...props }) => (
        <QueryMultipleData queries={queryResults} error={CustomError}>
            {(queries, reloadAll) => (
                <Component queryResults={queries} reloadAll={reloadAll} {...props} />
            )}
        </QueryMultipleData>
    );

    return WrappedComponent;
};

/**
 * HOC: withInfiniteQueryData
 * @param {React.ComponentType} Component - The component to wrap. Should accept props:
 *   - data: Array of pages from the infinite query
 *   - reload: Function to refetch the query
 *   - fetchNextPage: Function to fetch the next page
 *   - hasNextPage: Boolean indicating if more pages are available
 *   - isFetching: Boolean indicating loading state (including next page)
 *   - Any additional props passed to the HOC
 * @param {React.ComponentType} [CustomError] - Optional custom error component. Receives props:
 *   - error: The error object from the query
 *   - onReload: Function to refetch the query
 *
 * The HOC expects a prop:
 *   - queryResults: The result object from a useInfiniteQuery hook
 */
export const withInfiniteQueryData = (Component, CustomError) => {
    const WrappedComponent = forwardRef(({ queryResults, ...props }, ref) => {
        const { reauthenticate } = useAuthentication();

        const {
            data,
            error,
            isLoading,
            isError,
            isFetching,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
            refetch
        } = queryResults;

        useEffect(() => {
            if (isError && error?.cause?.status === 401) {
                reauthenticate();
            }
        }, [isError, error, reauthenticate]);

        // handle loading or fetch errors
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isError) {
            const ErrorComponent = CustomError || GenericError;
            return <ErrorComponent error={error} onReload={refetch} />;
        }

        return (
            <Component
                data={data?.pages || []}
                reload={refetch}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetching={isFetching || isFetchingNextPage}
                {...props}
                ref={ref}
            />
        );
    });

    return WrappedComponent;
};